import { useState } from 'react';
import type { MockCAPA } from '@/mocks/qms';
import { qcService } from '@/services/qcService';
import { useToast } from '@/contexts/ToastContext';

interface Props {
  capa: MockCAPA | null;
  onClose: () => void;
  onVerified: (capa: MockCAPA) => void;
}

export default function CAPAVerifyModal({ capa, onClose, onVerified }: Props) {
  const toast = useToast();
  const [effectiveness, setEffectiveness] = useState<MockCAPA['effectiveness']>('EFFECTIVE');
  const [saving, setSaving] = useState(false);

  if (!capa) return null;

  const verify = async () => {
    if (!effectiveness) {
      toast.error('Please select effectiveness rating');
      return;
    }

    try {
      setSaving(true);
      const now = new Date().toISOString().split('T')[0];
      const updatedCAPA: MockCAPA = {
        ...capa,
        status: 'VERIFIED',
        effectiveness,
        verifiedBy: 'Quality Lead',
        verifiedDate: now,
      };

      try {
        await qcService.verifyCAPA(capa.id, { effectiveness });
        if (capa.ncrId) {
          await qcService.updateNCRStatus(capa.ncrId, { status: 'CAPA_VERIFIED' });
        }
      } catch (err) {
        console.warn('Backend update failed, applying local state update:', err);
      }

      onVerified(updatedCAPA);
      toast.success(`CAPA ${capa.capaNumber} verified — NCR updated to CAPA Verified`);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to verify CAPA');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
            <i className="ri-shield-check-line text-violet-600 text-lg" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Verify CAPA</h3>
            <p className="text-sm text-gray-500">{capa.capaNumber}</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Review the effectiveness of the corrective and preventive actions taken for this CAPA.
          This will also update the linked NCR status to <strong>CAPA Verified</strong>.
        </p>

        <div className="mb-6">
          <label className="text-xs font-medium text-gray-500 mb-2 block">Effectiveness Rating</label>
          <div className="space-y-2">
            {(
              [
                { value: 'EFFECTIVE' as const, label: 'Effective', desc: 'Problem fully resolved, no recurrence' },
                { value: 'PARTIALLY_EFFECTIVE' as const, label: 'Partially Effective', desc: 'Improvement observed but monitoring needed' },
                { value: 'NOT_EFFECTIVE' as const, label: 'Not Effective', desc: 'Problem persists, further action required' },
              ] as const
            ).map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  effectiveness === opt.value
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="effectiveness"
                  value={opt.value}
                  checked={effectiveness === opt.value}
                  onChange={() => setEffectiveness(opt.value)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                  <p className="text-xs text-gray-500">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={verify}
            className="px-4 py-2 bg-violet-600 text-white rounded-md text-sm font-medium hover:bg-violet-700 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-check-line mr-1" />
            Verify CAPA
          </button>
        </div>
      </div>
    </div>
  );
}